// Constants for scroll behavior
const SCROLL_THRESHOLD = 100; // pixels from bottom to consider "at bottom"
const SCROLL_DELAY = 10; // ms delay before scroll action
const LOADING_INDICATOR_HEIGHT = 60; // approximate height of loading indicator

/**
 * Smart auto-scroll for chat interfaces
 * Only scrolls if user is near the bottom, respecting manual scroll position
 *
 * @param {boolean} smooth - Use smooth scrolling animation
 * @param {boolean} respectLoadingIndicator - Leave space for loading indicator
 * @param {number} offsetFromBottom - Additional offset from absolute bottom
 */
export const scrollToBottom = (
  smooth = true,
  respectLoadingIndicator = false,
  offsetFromBottom = 0
) => {
  // Try SparkLayout's scroll container first, then fall back to chat-messages
  const scrollRef = document.querySelector("#spark-scroll-container") || document.querySelector("#chat-messages");
  if (!scrollRef) return;

  // Calculate target scroll position
  let targetScroll = scrollRef.scrollHeight;

  // Adjust for loading indicator if needed
  if (respectLoadingIndicator) {
    targetScroll -= LOADING_INDICATOR_HEIGHT;
  }

  // Apply additional offset
  targetScroll -= offsetFromBottom;

  const scrollBehavior = smooth ? "smooth" : "instant";

  scrollRef.scrollTo({
    top: targetScroll,
    behavior: scrollBehavior,
  });
};

/**
 * Conditional auto-scroll during streaming
 * Only scrolls if user is near the bottom of the chat
 *
 * @param {boolean} smooth - Use smooth scrolling animation
 * @param {boolean} respectLoadingIndicator - Leave space for loading indicator
 * @param {number} threshold - Distance from bottom (in pixels) to consider "at bottom"
 */
export const conditionalAutoScroll = (
  smooth = true,
  respectLoadingIndicator = false,
  threshold = SCROLL_THRESHOLD
) => {
  // NOTE: must check the SAME element scrollToBottom() actually scrolls.
  // #chat-messages (the inner messages div) doesn't scroll itself — it has
  // no overflow-y-auto of its own, it just grows inside #spark-scroll-
  // container (its actual scrolling parent, from SparkLayout). Checking
  // #chat-messages's own scrollTop/scrollHeight there is always "at bottom"
  // (scrollTop is permanently 0), so this check would always pass regardless
  // of where the user actually scrolled — forcing them back to the bottom on
  // every throttled streaming tick even after they scrolled up to read.
  const scrollRef = document.querySelector("#spark-scroll-container") || document.querySelector("#chat-messages");
  if (!scrollRef) return;

  // Check if user is near the bottom
  const isAtBottom =
    scrollRef.scrollHeight - scrollRef.scrollTop <= scrollRef.clientHeight + threshold;

  if (isAtBottom) {
    scrollToBottom(smooth, respectLoadingIndicator);
  }
};

/**
 * Throttled auto-scroll for streaming content
 * Reduces scroll frequency during rapid content updates
 *
 * @param {string} content - Current content (to check length)
 * @param {number} interval - Scroll every N characters (default: 100)
 */
export const throttledStreamScroll = (
  content,
  interval = 100,
  respectLoadingIndicator = false
) => {
  if (content.length % interval === 0) {
    setTimeout(() => {
      conditionalAutoScroll(true, respectLoadingIndicator);
    }, SCROLL_DELAY);
  }
};

// Export constants for consistency across the app
export const SCROLL_CONFIG = {
  THRESHOLD: SCROLL_THRESHOLD,
  DELAY: SCROLL_DELAY,
  LOADING_INDICATOR_HEIGHT,
  STREAM_INTERVAL: 100, // characters between scroll checks
};
